/**
 * Player (phone) end-game view. Winner banner, your race-bet resolutions, and
 * the final standings — styled to match the betting-app wallet.
 */

import { Button } from '@mantine/core'
import { Link } from 'react-router-dom'
import { PLAYER_COLOR_HEX, type Room } from '@mdev/common'
import { CAMEL_HEX, CU, CU_DISPLAY, CU_HEADING } from '../theme'
import { CamelGlyph } from './CamelGlyph'
import { Coins, Panel, PanelTitle } from './pieces'
import type { CamelUpSettings, CamelUpState } from '../module'

interface Props {
  room: Room<CamelUpSettings>
  state: CamelUpState
  uid: string
}

function delta(n: number) {
  return n >= 0 ? `+${n}` : `${n}`
}

export function PlayerFinishedView(props: Props) {
  const { room, state, uid } = props
  const winner = state.winner ? state.players[state.winner.uid] : null
  const iWon = winner?.uid === uid
  const review = state.raceReview
  const myRow = review?.rows.find((r) => r.uid === uid)
  const myLegRow = state.legReview?.rows.find((r) => r.uid === uid)
  const ranked = [...state.seatOrder].sort((a, b) => state.players[b].coins - state.players[a].coins)

  return (
    <div
      style={{
        background: CU.pageBg,
        minHeight: '100%',
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        maxWidth: 520,
        margin: '0 auto',
      }}
    >
      {winner && (
        <div
          style={{
            borderRadius: 18,
            padding: 18,
            background: iWon
              ? `linear-gradient(135deg, ${CU.coin}, ${CU.coinDeep})`
              : 'linear-gradient(160deg, #fdf3da, #f0dcab)',
            border: `2px solid ${iWon ? CU.coinDeep : CU.border}`,
            color: '#3a2a14',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            boxShadow: CU.shadow,
          }}
        >
          <span style={{ fontSize: 36 }}>{iWon ? '🏆' : '🐫'}</span>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontFamily: CU_DISPLAY, fontSize: 24, lineHeight: 1 }}>
              {iWon ? 'You won!' : `${winner.displayName} wins`}
            </span>
            <span style={{ fontFamily: CU_HEADING, fontSize: 14 }}>{state.winner!.coins} coins</span>
          </div>
        </div>
      )}

      {room.nextCode ? (
        <Button
          component={Link}
          to={`/games/${room.gameId}/play/${room.nextCode}/new`}
          color="yellow"
          size="lg"
          fullWidth
        >
          Join next game →
        </Button>
      ) : (
        <Button color="gray" variant="light" size="lg" fullWidth disabled>
          Waiting for the host to start a new game…
        </Button>
      )}

      {review && (
        <Panel pad={16}>
          <div style={{ display: 'flex', gap: 24 }}>
            <ResultMini label="Winner" color={review.raceWinner} />
            <ResultMini label="Loser" color={review.raceLoser} />
          </div>
        </Panel>
      )}

      {myRow && (
        <Panel pad={16}>
          <PanelTitle>Your race bets</PanelTitle>
          {myRow.bets.length === 0 ? (
            <span style={{ fontSize: 13, color: CU.inkSoft }}>You didn't place any race bets.</span>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {myRow.bets.map((b, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '3px 10px',
                      borderRadius: 999,
                      fontSize: 13,
                      fontFamily: CU_HEADING,
                      color: CAMEL_HEX[b.color].ink,
                      background: `linear-gradient(135deg, ${CAMEL_HEX[b.color].light}, ${CAMEL_HEX[b.color].dark})`,
                    }}
                  >
                    {b.pile === 'winner' ? 'Winner' : 'Loser'} · {b.color}
                  </span>
                  <span style={{ fontFamily: CU_HEADING, fontWeight: 700, color: b.payout >= 0 ? '#1DA64A' : CU.accent }}>
                    {delta(b.payout)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Panel>
      )}

      {myLegRow && (
        <Panel pad={16}>
          <PanelTitle>Final leg payout</PanelTitle>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '3px 10px',
                borderRadius: 999,
                fontSize: 13,
                fontFamily: CU_HEADING,
                color: CU.brass,
                border: `1px solid ${CU.brass}`,
              }}
            >
              pyramid {delta(myLegRow.pyramidCoins)}
            </span>
            {myLegRow.legBetBreakdown.map((b, i) => (
              <span
                key={i}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '3px 10px',
                  borderRadius: 999,
                  fontSize: 13,
                  fontFamily: CU_HEADING,
                  color: CAMEL_HEX[b.color].ink,
                  background: `linear-gradient(135deg, ${CAMEL_HEX[b.color].light}, ${CAMEL_HEX[b.color].dark})`,
                }}
              >
                {b.color} {b.value} · {delta(b.payout)}
              </span>
            ))}
            {myLegRow.legBetBreakdown.length === 0 && (
              <span style={{ fontSize: 13, color: CU.inkSoft }}>no leg bets</span>
            )}
            <span style={{ marginLeft: 'auto', fontFamily: CU_HEADING, fontWeight: 700, color: (myLegRow.pyramidCoins + myLegRow.legBetCoins) >= 0 ? '#1DA64A' : CU.accent }}>
              {delta(myLegRow.pyramidCoins + myLegRow.legBetCoins)}
            </span>
          </div>
        </Panel>
      )}

      <Panel pad={16}>
        <PanelTitle>Final standings</PanelTitle>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {ranked.map((u, i) => {
            const p = state.players[u]
            return (
              <div
                key={u}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '6px 8px',
                  borderRadius: 8,
                  background: u === uid ? CU.surfaceSolid : 'transparent',
                  borderLeft: `4px solid ${PLAYER_COLOR_HEX[p.color]}`,
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontFamily: CU_HEADING, fontWeight: 900, color: i === 0 ? CU.brass : CU.inkSoft, width: 18 }}>
                    {i + 1}
                  </span>
                  <span style={{ fontSize: 16 }}>{p.avatar}</span>
                  <span style={{ fontFamily: CU_HEADING, color: CU.ink }}>
                    {p.displayName}
                    {u === uid && <span style={{ color: CU.inkSoft }}> (you)</span>}
                  </span>
                </span>
                <Coins amount={p.coins} />
              </div>
            )
          })}
        </div>
      </Panel>
    </div>
  )
}

function ResultMini({ label, color }: { label: string; color: CamelUpState['finished'][number] }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontFamily: CU_HEADING, fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: CU.inkSoft }}>
        {label}
      </span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <CamelGlyph color={color} size={22} noShadow />
        <span style={{ fontFamily: CU_HEADING, textTransform: 'capitalize', color: CU.ink }}>{color}</span>
      </span>
    </div>
  )
}
